import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["onMouseEnter", "onMouseLeave", "onFocus", "onBlur"];
import { callAllHandlers } from "@smartsheet/lodestar-utils";
import { useCallback, useEffect, useRef, useState } from "react";
// Helps control delay when hovering back-to-back over items w/ flyouts.
// Hovering back-to-back when flyout is showing will ignore initial flyout delay.
var ignoreEnterDelay = false;
var ignoreEnterDelayTimer;
var closeDelayTimer;

// We don't want to allow tests to interact with each other. Sometimes if a Flyout doesn't
// go away quickly enough (tooltips, for instance) the next test will render one before
// these flags are reset. We always want to reset these between tests. See jest.setup.ts.
var resetUseDelayedOptionForTest = function resetUseDelayedOptionForTest() {
  return undefined;
};
if (process.env.NODE_ENV !== "production") {
  // Don't ship this code in production.
  resetUseDelayedOptionForTest = function resetUseDelayedOptionForTest() {
    ignoreEnterDelay = false;
    clearTimeout(ignoreEnterDelayTimer);
  };
}
export { resetUseDelayedOptionForTest };

/**
 * This hook provides an isOpen flag and HTML handlers to attach to an element to mutate it. It can
 * be used for elements that should show a popup after a short time like a contact card, tooltip,
 * etc. For things like tooltips, it supports a delayMode argument that allows the floating elements
 * to interact with each other so that opening subsequent elements is faster after the first one
 * opens. This provides a nice UX when a user is trying to look at multiple tooltips.
 */
export var useDelayedOpen = function useDelayedOpen() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    exitDelay = _ref.exitDelay,
    _ref$enterDelay = _ref.enterDelay,
    enterDelay = _ref$enterDelay === void 0 ? 500 : _ref$enterDelay,
    _ref$resetDelay = _ref.resetDelay,
    resetDelay = _ref$resetDelay === void 0 ? 500 : _ref$resetDelay,
    _ref$delayMode = _ref.delayMode,
    delayMode = _ref$delayMode === void 0 ? "batch" : _ref$delayMode;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isOpen = _useState2[0],
    setIsOpen = _useState2[1];
  var enterTimer = useRef();
  var handleOpen = useCallback(function () {
    window.clearTimeout(ignoreEnterDelayTimer);
    if (delayMode === "batch") {
      ignoreEnterDelay = true;
    }
    setIsOpen(true);
  }, [delayMode]);
  var handleClose = useCallback(function () {
    setIsOpen(false);
  }, []);
  var cancelExitDelayTimer = useCallback(function () {
    if (exitDelay) {
      window.clearTimeout(closeDelayTimer);
    }
  }, [exitDelay]);
  var maybeDelayedOpen = useCallback(function () {
    // This ensures that the flyout doesn't open after you have quickly moved away from element.
    window.clearTimeout(enterTimer.current);
    cancelExitDelayTimer();
    if (delayMode === "each" || !ignoreEnterDelay) {
      enterTimer.current = window.setTimeout(function () {
        handleOpen();
      }, enterDelay);
    } else {
      handleOpen();
    }
  }, [delayMode, enterDelay, handleOpen, cancelExitDelayTimer]);
  var maybeDelayedClose = useCallback(function () {
    // this ensures that the flyout doesnt open after you have quickly moved away from element.
    window.clearTimeout(enterTimer.current);
    window.clearTimeout(ignoreEnterDelayTimer);
    if (delayMode === "batch") {
      ignoreEnterDelayTimer = window.setTimeout(function () {
        ignoreEnterDelay = false;
      }, resetDelay);
    }
    if (exitDelay) {
      cancelExitDelayTimer();
      closeDelayTimer = window.setTimeout(function () {
        handleClose();
      }, exitDelay);
    } else {
      handleClose();
    }
  }, [handleClose, delayMode, resetDelay, exitDelay, cancelExitDelayTimer]);

  // Clean up timers when unmounted.
  useEffect(function () {
    return function () {
      window.clearTimeout(closeDelayTimer);
      window.clearTimeout(enterTimer.current);
      window.clearTimeout(ignoreEnterDelayTimer);
    };
  }, []);
  return {
    setIsOpen: useCallback(function (newIsOpen) {
      return newIsOpen ? maybeDelayedOpen() : maybeDelayedClose();
    }, [maybeDelayedOpen, maybeDelayedClose]),
    cancelEnterTimer: useCallback(function () {
      window.clearTimeout(enterTimer.current);
    }, []),
    isOpen: isOpen,
    getDelayedOpenTargetProps: function getDelayedOpenTargetProps() {
      var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
        onMouseEnter = _ref2.onMouseEnter,
        onMouseLeave = _ref2.onMouseLeave,
        onFocus = _ref2.onFocus,
        onBlur = _ref2.onBlur,
        restOverrides = _objectWithoutProperties(_ref2, _excluded);
      return _objectSpread(_objectSpread({}, restOverrides), {}, {
        onMouseEnter: callAllHandlers(onMouseEnter, maybeDelayedOpen),
        onMouseLeave: callAllHandlers(onMouseLeave, maybeDelayedClose),
        onFocus: callAllHandlers(onFocus, maybeDelayedOpen),
        onBlur: callAllHandlers(onBlur, maybeDelayedClose)
      });
    },
    cancelExitDelayTimer: cancelExitDelayTimer
  };
};
//# sourceMappingURL=useDelayedOpen.js.map